/**
 * Date formatter — Intl-based, locale-aware.
 */
import { useTranslation } from "react-i18next";

import type { FormatDateOptions } from "./types";

export function formatDate(
  date: Date | string | null | undefined,
  options?: FormatDateOptions,
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";

  const locale =
    options?.locale ??
    (typeof document !== "undefined" &&
    document.documentElement.lang === "ar"
      ? "ar-SA"
      : "en-US");

  const fmt: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: options?.month ?? "long",
    day: "numeric",
    ...(options?.includeTime
      ? { hour: "2-digit", minute: "2-digit" }
      : {}),
  };
  return new Intl.DateTimeFormat(locale, fmt).format(d);
}

/**
 * Relative time ("2 days ago", "in 3 hours"). Uses Intl.RelativeTimeFormat.
 */
export function relativeDate(
  date: Date | string | null | undefined,
  locale?: string,
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";

  const loc =
    locale ??
    (typeof document !== "undefined" &&
    document.documentElement.lang === "ar"
      ? "ar-SA"
      : "en-US");
  const rtf = new Intl.RelativeTimeFormat(loc, { numeric: "auto" });

  const diffSeconds = Math.round((d.getTime() - Date.now()) / 1000);
  const absSec = Math.abs(diffSeconds);

  // Pick the appropriate unit.
  if (absSec < 60) return rtf.format(diffSeconds, "second");
  if (absSec < 3600) return rtf.format(Math.round(diffSeconds / 60), "minute");
  if (absSec < 86400) return rtf.format(Math.round(diffSeconds / 3600), "hour");
  if (absSec < 2592000) return rtf.format(Math.round(diffSeconds / 86400), "day");
  if (absSec < 31536000) return rtf.format(Math.round(diffSeconds / 2592000), "month");
  return rtf.format(Math.round(diffSeconds / 31536000), "year");
}

export interface DateDisplayProps {
  date: Date | string | null | undefined;
  format?: "long" | "short" | "numeric";
  showRelative?: boolean;
  includeTime?: boolean;
  className?: string;
}

export function DateDisplay({
  date,
  format = "long",
  showRelative = false,
  includeTime = false,
  className,
}: DateDisplayProps) {
  const { i18n } = useTranslation();
  const monthFmt =
    format === "short" ? "short" : format === "numeric" ? "numeric" : "long";
  const absolute = formatDate(date, {
    locale: i18n.language as "en-US" | "ar-SA",
    month: monthFmt,
    includeTime,
  });
  if (!absolute) return <span className="text-text-tertiary">—</span>;

  if (showRelative) {
    const rel = relativeDate(date, i18n.language);
    return (
      <span className={className} title={rel}>
        {absolute}
      </span>
    );
  }
  return <span className={className}>{absolute}</span>;
}
